import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, PiggyBank, Plus, Trash2, Target, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

export default function SavingsPage() {
  const navigate = useNavigate();
  const { hasAccess } = useAuth();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newGoal, setNewGoal] = useState({
    name: '',
    targetAmount: '',
    currentAmount: '',
    deadline: ''
  });

  useEffect(() => {
    if (hasAccess) {
      const saved = localStorage.getItem('savings-goals');
      if (saved) {
        setGoals(JSON.parse(saved));
      }
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  const saveGoals = (updated) => {
    setGoals(updated);
    localStorage.setItem('savings-goals', JSON.stringify(updated));
  };

  const addGoal = () => {
    if (!newGoal.name.trim() || !newGoal.targetAmount) {
      toast.error('Ange namn och målbelopp');
      return;
    }
    const goal = {
      id: Date.now(),
      name: newGoal.name,
      targetAmount: Number(newGoal.targetAmount),
      currentAmount: Number(newGoal.currentAmount) || 0,
      deadline: newGoal.deadline || null,
      createdAt: new Date().toISOString()
    };
    saveGoals([...goals, goal]);
    setNewGoal({ name: '', targetAmount: '', currentAmount: '', deadline: '' });
    setShowForm(false);
    toast.success('Sparmål skapat!');
  };

  const updateAmount = (goalId, amount) => {
    const updated = goals.map(g => {
      if (g.id === goalId) {
        const newAmount = Math.max(0, g.currentAmount + amount);
        return { ...g, currentAmount: newAmount };
      }
      return g;
    });
    saveGoals(updated);
    toast.success(amount > 0 ? 'Insättning registrerad!' : 'Uttag registrerat');
  };

  const deleteGoal = (goalId) => {
    saveGoals(goals.filter(g => g.id !== goalId));
    toast.success('Sparmål borttaget');
  };

  const totalSaved = goals.reduce((sum, g) => sum + g.currentAmount, 0);
  const totalTarget = goals.reduce((sum, g) => sum + g.targetAmount, 0);

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background" data-testid="savings-page">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/economy')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-light">Sparmål</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12 text-center">
          <p className="text-muted-foreground">Uppgradera för att få tillgång till sparmål.</p>
          <Button onClick={() => navigate('/subscription')} className="rounded-full mt-4">Uppgradera nu</Button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="savings-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/category/economy')} className="rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-heading text-xl font-light">Sparmål</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <div className="text-center space-y-2">
          <PiggyBank className="w-12 h-12 mx-auto text-teal-500" />
          <h2 className="font-heading text-2xl font-semibold">Sparmål</h2>
          <p className="text-muted-foreground">Sätt mål och följ ditt sparande</p>
        </div>

        {/* Summary */}
        <div className="bg-teal-50 rounded-2xl p-4 border border-teal-100">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-teal-700">Totalt sparat</p>
              <p className="text-2xl font-bold text-teal-800">{totalSaved.toLocaleString('sv-SE')} kr</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-teal-700">Av totalt mål</p>
              <p className="text-2xl font-bold text-teal-800">{totalTarget.toLocaleString('sv-SE')} kr</p>
            </div>
          </div>
          {totalTarget > 0 && (
            <div className="mt-3">
              <div className="h-2 bg-teal-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-teal-500 transition-all rounded-full"
                  style={{ width: `${Math.min(100, (totalSaved / totalTarget) * 100)}%` }}
                />
              </div>
              <p className="text-xs text-teal-600 mt-1">
                {Math.round((totalSaved / totalTarget) * 100)}% av totalt mål
              </p>
            </div>
          )}
        </div>

        {/* Add button */}
        <Button onClick={() => setShowForm(true)} className="w-full rounded-full" variant="outline">
          <Plus className="w-5 h-5 mr-2" />
          Nytt sparmål
        </Button>

        {/* Add form */}
        {showForm && (
          <div className="bg-white rounded-2xl p-6 border border-border/30 space-y-4">
            <h3 className="font-semibold">Skapa sparmål</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="text-sm text-muted-foreground">Vad sparar du till? *</label>
                <Input
                  value={newGoal.name}
                  onChange={(e) => setNewGoal({ ...newGoal, name: e.target.value })}
                  placeholder="T.ex. Semester, Bil, Buffert"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Målbelopp (kr) *</label>
                <Input
                  type="number"
                  value={newGoal.targetAmount}
                  onChange={(e) => setNewGoal({ ...newGoal, targetAmount: e.target.value })}
                  placeholder="T.ex. 50000"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Redan sparat (kr)</label>
                <Input
                  type="number"
                  value={newGoal.currentAmount}
                  onChange={(e) => setNewGoal({ ...newGoal, currentAmount: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Måldatum (valfritt)</label>
                <Input
                  type="date"
                  value={newGoal.deadline}
                  onChange={(e) => setNewGoal({ ...newGoal, deadline: e.target.value })}
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowForm(false)} className="rounded-full">
                Avbryt
              </Button>
              <Button onClick={addGoal} className="rounded-full">
                Skapa mål
              </Button>
            </div>
          </div>
        )}

        {/* Goals list */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-teal-200 border-t-teal-500 rounded-full animate-spin" />
          </div>
        ) : goals.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Inga sparmål än. Skapa ditt första!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {goals.map(goal => {
              const progress = Math.min(100, (goal.currentAmount / goal.targetAmount) * 100);
              const remaining = goal.targetAmount - goal.currentAmount;
              return (
                <div key={goal.id} className="bg-white rounded-2xl p-5 border border-border/30">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-semibold">{goal.name}</h4>
                      <p className="text-sm text-muted-foreground">
                        {goal.currentAmount.toLocaleString('sv-SE')} kr av {goal.targetAmount.toLocaleString('sv-SE')} kr
                      </p>
                    </div>
                    <button
                      onClick={() => deleteGoal(goal.id)}
                      className="p-2 text-gray-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden mb-2">
                    <div 
                      className={`h-full transition-all rounded-full ${progress >= 100 ? 'bg-green-500' : 'bg-teal-500'}`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  
                  <div className="flex justify-between items-center text-sm">
                    <span className={progress >= 100 ? 'text-green-600 font-semibold' : 'text-muted-foreground'}>
                      {progress >= 100 ? '🎉 Mål uppnått!' : `${remaining.toLocaleString('sv-SE')} kr kvar`}
                    </span>
                    <span className="text-muted-foreground">{Math.round(progress)}%</span>
                  </div>

                  {/* Quick add/remove buttons */}
                  <div className="flex gap-2 mt-4">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => updateAmount(goal.id, -500)}
                      className="rounded-full flex-1"
                    >
                      - 500 kr
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => updateAmount(goal.id, 500)}
                      className="rounded-full flex-1"
                    >
                      + 500 kr
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => updateAmount(goal.id, 1000)}
                      className="rounded-full flex-1"
                    >
                      + 1000 kr
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Tips */}
        <div className="bg-teal-50 rounded-2xl p-4 border border-teal-100">
          <p className="text-teal-800 text-sm">
            💡 <strong>Tips:</strong> Sätt upp automatiska överföringar till sparkonto direkt efter lön – "betala dig själv först"!
          </p>
        </div>
      </main>
    </div>
  );
}
